import { AbstractControl, FormArray, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { LocalTimeRange } from '@ftr/contracts/type/shared'
import { DateFormat, jodaFormatPatternWithLocale } from '@ftr/foundation'
import { Duration } from '@js-joda/core'
import { TimeRangeControl, TimeRangeControlFormValue, isFilledOut, isTimeRangeControl } from './time-range-control'

const SIXTEEN_HOURS_TO_MS = 16 * 60 * 60 * 1000

/**
 * With optional ranges, it is invalid to only supply start without end.
 *
 * With required ranges, the inputs themselves are required so we dont have this issue
 */
export function allOrNothing(inputValidation: ValidatorFn[] = []): ValidatorFn {
  return (control: AbstractControl) => {
    if (isTimeRangeControl(control)) {
      const isRequired = inputValidation.some(validator => validator === Validators.required)

      if (!isRequired && !!control.controls.start.value !== !!control.controls.end.value) {
        return {
          allOrNothing: 'Both start time and end time must be specified.',
        }
      }
    }

    return null
  }
}

export function isStartAfterEnd(): ValidatorFn {
  return (control: AbstractControl) => {
    if (isTimeRangeControl(control)) {
      const startValue = control.controls.start.value
      const endValue = control.controls.end.value

      if (!startValue || !endValue) {
        return null
      }

      if (startValue.equals(endValue) || startValue.isAfter(endValue)) {
        return {
          startAfterEnd: 'The end time must be after the start time.',
        }
      }
    }

    return null
  }
}

export function isWithinTimeRange(
  scheduleControl: TimeRangeControl,
  errorMessage: string = 'Session times cannot be outside recording times, this session should be within',
  timeFormat: DateFormat = DateFormat.Time,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const self = control as TimeRangeControl
    const scheduleRawValue = scheduleControl.getRawValue()

    if (
      isFilledOut(scheduleRawValue) &&
      isFilledOut(self.getRawValue()) &&
      !isWithinOptional(scheduleRawValue, self.getRawValue())
    ) {
      const startTimeString = scheduleControl.value.start?.format(jodaFormatPatternWithLocale(timeFormat))
      const endTimeString = scheduleControl.value.end?.format(jodaFormatPatternWithLocale(timeFormat))
      return {
        overlapping: `${errorMessage} ${startTimeString} - ${endTimeString}`,
      }
    }
    return null
  }
}

export function isLessThan16Hours(scheduleControl: TimeRangeControl): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const self = control as TimeRangeControl
    const scheduleRawValue = scheduleControl.getRawValue()

    if (isFilledOut(scheduleRawValue) && isFilledOut(self.getRawValue())) {
      const scheduleDuration = Duration.between(scheduleControl.value!.start!, scheduleControl.value!.end!).toMillis()
      if (scheduleDuration > SIXTEEN_HOURS_TO_MS) {
        return {
          overlapping: 'Schedule time range cannot be more than 16 hours',
        }
      }
    }
    return null
  }
}

export function isNotOverlappingTimeRange(
  formArray: FormArray<TimeRangeControl>,
  errorMessage: string = 'This time range overlaps with',
  timeFormat: DateFormat = DateFormat.Time,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null
    }

    const self = control as TimeRangeControl

    const allControls: TimeRangeControl[] = formArray.controls
    const withoutSelf = allControls.filter(x => x !== control)
    const overlapping = withoutSelf.find(c => isOverlappingOptional(self.getRawValue(), c.getRawValue()))

    if (overlapping) {
      const startTimeString = overlapping.value.start?.format(jodaFormatPatternWithLocale(timeFormat))
      const endTimeString = overlapping.value.end?.format(jodaFormatPatternWithLocale(timeFormat))
      return {
        overlapping: `${errorMessage} ${startTimeString} - ${endTimeString}`,
      }
    }

    return null
  }
}

export function isScheduleFilled(scheduleControl: TimeRangeControl): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null
    }
    const self = control as TimeRangeControl
    const scheduleRawValue = scheduleControl.getRawValue()
    if (!isFilledOut(scheduleRawValue) && isFilledOut(self.getRawValue())) {
      return {
        overlapping: 'Cannot have session times without a recording schedule',
      }
    }
    return null
  }
}

export function isWithinOptional(
  timeframe1: Partial<TimeRangeControlFormValue>,
  timeframe2: Partial<TimeRangeControlFormValue>,
): boolean {
  return (
    isFilledOut(timeframe1) &&
    isFilledOut(timeframe2) &&
    new LocalTimeRange(timeframe1.start, timeframe1.end).encloses(new LocalTimeRange(timeframe2.start, timeframe2.end))
  )
}

export function isOverlappingOptional(
  timeframe1: Partial<TimeRangeControlFormValue>,
  timeframe2: Partial<TimeRangeControlFormValue>,
): boolean {
  return (
    isFilledOut(timeframe1) &&
    isFilledOut(timeframe2) &&
    new LocalTimeRange(timeframe1.start, timeframe1.end).overlaps(new LocalTimeRange(timeframe2.start, timeframe2.end))
  )
}
